<template>
  <div>
    <BenefitsSidePanel
      v-model="panelOpen"
      :title="title"
      :close-label="t('benefitsCenter-points-panel-close')"
      width="700px"
      @close="handleClose"
    >
      <div class="benefits-reward-panel">
      <section class="benefits-reward-panel-section">
        <div v-if="hasTiers" class="benefits-reward-field">
          <span class="benefits-reward-field-label-text">
            {{ t("benefitsCenter-points-panel-tiers") }}
          </span>
          <KvSelect
            v-model="draft.tierIds"
            :options="tierOptions"
            multiple
            variant="outlined"
            size="large"
            full-width
            :placeholder="t('benefitsCenter-points-panel-tiersPlaceholder')"
          >
            <template #chip="{ option }">
              <BenefitsTierChip :option="option" />
            </template>
          </KvSelect>
          <p v-if="!tiersEnabled" class="benefits-reward-field-hint">{{ t("benefitsCenter-points-panel-tiersDisabledHint") }}</p>
        </div>

        <div
          class="benefits-reward-fields-row benefits-reward-fields-row--meta"
          :class="{ 'benefits-reward-fields-row--with-privacy': hasTiers }"
        >
          <div class="benefits-reward-field benefits-reward-field--points">
            <label class="benefits-reward-field-label" for="benefits-reward-points">
              <span class="benefits-reward-field-label-text">
                {{ t("benefitsCenter-points-panel-points") }}
                <span class="benefits-required-asterisk">*</span>
              </span>
            </label>
            <div class="benefits-reward-field-control">
              <KvInput
                id="benefits-reward-points"
                v-model.number="draft.points"
                type="number"
                :min="1"
                variant="outlined"
                size="large"
                :placeholder="t('benefitsCenter-points-panel-pointsPlaceholder')"
              />
            </div>
          </div>

          <div v-if="hasTiers" class="benefits-reward-field">
            <span class="benefits-reward-field-label-text">
              {{ t("benefitsCenter-points-panel-privacy") }}
              <span class="benefits-required-asterisk">*</span>
            </span>
            <KvSelect
              v-model="draft.privacy"
              :options="privacyOptions"
              value-field="value"
              label-field="label"
              variant="outlined"
              size="large"
              full-width
            />
          </div>

          <div class="benefits-reward-field">
            <span class="benefits-reward-field-label">
              <span class="benefits-reward-field-label-text">
                {{ t("benefitsCenter-points-panel-startDate") }}
              </span>
              <button
                v-if="draft.startDate"
                type="button"
                class="benefits-reward-clear-date"
                @click="draft.startDate = ''"
              >
                {{ t("benefitsCenter-points-panel-clearDate") }}
              </button>
            </span>
            <div class="benefits-reward-field-control">
              <KvDatePicker
                v-model="draft.startDate"
                value-format="yyyy-MM-dd"
                placeholder="dd/mm/aaaa"
                variant="outlined"
                size="large"
                placement="bottom left"
              />
            </div>
          </div>

          <div class="benefits-reward-field">
            <span class="benefits-reward-field-label">
              <span class="benefits-reward-field-label-text">
                {{ t("benefitsCenter-points-panel-endDate") }}
              </span>
              <button
                v-if="draft.endDate"
                type="button"
                class="benefits-reward-clear-date"
                @click="draft.endDate = ''"
              >
                {{ t("benefitsCenter-points-panel-clearDate") }}
              </button>
            </span>
            <div class="benefits-reward-field-control">
              <KvDatePicker
                v-model="draft.endDate"
                value-format="yyyy-MM-dd"
                placeholder="dd/mm/aaaa"
                variant="outlined"
                size="large"
                placement="bottom left"
              />
            </div>
          </div>
        </div>

        <section class="benefits-reward-field">
          <span class="benefits-reward-field-label-text">
            {{ t("benefitsCenter-points-panel-image") }}
          </span>
          <KvImageInput
            v-model="draft.image"
            variant="outlined"
            size="large"
            :title="t('benefitsCenter-points-panel-imageUploadTitle')"
            :description="t('benefitsCenter-points-panel-imageUploadDesc')"
            :button-label="t('benefitsCenter-points-panel-imageUploadButton')"
            :delete-label="t('benefitsCenter-points-panel-imageUploadDelete')"
            accept="image/png, image/jpeg, image/jpg, image/webp"
          />
        </section>

        <div class="benefits-reward-field">
          <label class="benefits-reward-field-label" for="benefits-reward-name">
            <span class="benefits-reward-field-label-text">
              {{ t("benefitsCenter-points-panel-name") }}
              <span class="benefits-required-asterisk">*</span>
            </span>
          </label>
          <KvInput
            id="benefits-reward-name"
            v-model="draft.name"
            variant="outlined"
            size="large"
            :placeholder="t('benefitsCenter-points-panel-namePlaceholder')"
          />
        </div>

        <div class="benefits-reward-field">
          <label class="benefits-reward-field-label" for="benefits-reward-subtitle">
            <span class="benefits-reward-field-label-text">
              {{ t("benefitsCenter-points-panel-subtitle") }}
            </span>
          </label>
          <KvInput
            id="benefits-reward-subtitle"
            v-model="draft.subtitle"
            variant="outlined"
            size="large"
            :placeholder="t('benefitsCenter-points-panel-subtitlePlaceholder')"
          />
        </div>

        <div class="benefits-reward-field">
          <label class="benefits-reward-field-label" for="benefits-reward-description">
            <span class="benefits-reward-field-label-text">
              {{ t("benefitsCenter-points-panel-description") }}
            </span>
          </label>
          <BenefitsRichText
            v-model="draft.description"
            :placeholder="t('benefitsCenter-points-panel-descriptionPlaceholder')"
            height="160px"
          />
        </div>

        <div class="benefits-reward-toggles">
          <div class="benefits-reward-toggle-field">
            <label class="benefits-reward-toggle-row">
              <span>{{ t("benefitsCenter-points-panel-clientCanUse") }}</span>
              <BenefitsToggle v-model="draft.canUse" />
            </label>
            <p class="benefits-reward-toggle-hint">
              {{
                draft.canUse
                  ? t("benefitsCenter-points-panel-clientCanUseHintOn")
                  : t("benefitsCenter-points-panel-clientCanUseHintOff")
              }}
            </p>
          </div>
        </div>

        <div
          class="benefits-reward-field"
          v-if="draft.canUse"
        >
          <label class="benefits-reward-field-label" for="benefits-reward-success-message">
            <span class="benefits-reward-field-label-text">
              {{ t("benefitsCenter-points-panel-successMessage") }}
              <span class="benefits-required-asterisk">*</span>
            </span>
          </label>
          <KvTextarea
            id="benefits-reward-success-message"
            v-model="draft.successMessage"
            variant="outlined"
            size="large"
            :rows="3"
            :resize="true"
            :placeholder="t('benefitsCenter-points-panel-successMessagePlaceholder')"
            @input="limitSuccessMessage"
          />
          <span
            class="benefits-reward-counter"
            :class="{ 'is-full': successMessageLength >= SUCCESS_MESSAGE_MAX }"
            >{{ successMessageLength }}/{{ SUCCESS_MESSAGE_MAX }}</span
          >
        </div>

        <div class="benefits-reward-toggle-field">
          <label class="benefits-reward-toggle-row">
            <span>{{ t("benefitsCenter-points-panel-howToUse") }}</span>
            <BenefitsToggle v-model="draft.hasUsageInstructions" />
          </label>
        </div>

        <p class="benefits-reward-toggles-alert" v-if="!hasAnyUsageOption">
          {{ t("benefitsCenter-points-panel-usageOptionRequired") }}
        </p>

        <div
          class="benefits-reward-field"
          v-if="draft.hasUsageInstructions"
        >
          <KvTextarea
            id="benefits-reward-instructions"
            v-model="draft.usageInstructions"
            variant="outlined"
            size="large"
            :rows="4"
            :resize="true"
            :placeholder="t('benefitsCenter-points-panel-howToUseInstructionsPlaceholder')"
          />
        </div>
      </section>
    </div>

    <template slot="footer">
      <div class="benefits-reward-panel-footer">
        <div class="benefits-reward-panel-footer-left">
          <KvButton
            class="benefits-reward-panel-cancel"
            type="button"
            variant="outlined"
            @click="close"
          >
            {{ t("benefitsCenter-points-panel-cancel") }}
          </KvButton>
        </div>
        <div class="benefits-reward-panel-footer-right">
          <button
            v-if="draft.id"
            class="benefits-reward-panel-delete"
            type="button"
            @click="deleteReward"
          >
            <Icon icon-class="ui-trash-01" size="4" color="#f04438" />
            {{ t("benefitsCenter-points-panel-delete") }}
          </button>
          <KvTooltip :text="missingFieldsTooltip" :disabled="canSave" placement="top">
            <KvButton
              class="benefits-reward-panel-save"
              type="button"
              :disabled="!canSave"
              @click="save"
            >
              <div style="display: flex; align-items: center; gap: 8px;">
                <Icon :icon-class="draft.id ? 'ui-check' : 'ui-plus'" size="4" />
                <span>{{ draft.id ? t("benefitsCenter-points-panel-saveChanges") : t("benefitsCenter-points-panel-createRewardButton") }}</span>
              </div>
            </KvButton>
          </KvTooltip>
        </div>
      </div>
    </template>

    </BenefitsSidePanel>

    <div v-if="deleteConfirmOpen" class="benefits-reward-delete-modal-backdrop" @click.self="closeDeleteConfirm">
      <KvCard class="benefits-reward-delete-modal" content-class="benefits-reward-delete-modal-content">
        <template #text>
          <KvButton class="benefits-reward-delete-modal-close" type="button" @click="closeDeleteConfirm" aria-label="Fechar" style="min-width: 0;">
            <Icon icon-class="ui-x-close" size="5" />
          </KvButton>
          
          <h4 class="benefits-reward-delete-modal-title">{{ t("benefitsCenter-points-deleteModalTitle") }}</h4>
          <p class="benefits-reward-delete-modal-desc">{{ t("benefitsCenter-points-deleteModalDescription") }}</p>

          <BenefitsRewardCard :reward="draft" readonly />

          <div class="benefits-reward-delete-modal-actions">
            <KvButton class="benefits-reward-delete-cancel" type="button" variant="outlined" @click="closeDeleteConfirm">
              {{ t("benefitsCenter-points-deleteModalCancel") }}
            </KvButton>

            <button class="benefits-reward-delete-confirm" type="button" @click="confirmDelete">
              <Icon icon-class="ui-trash-01" size="4" />
              {{ t("benefitsCenter-points-deleteModalConfirm") }}
            </button>
          </div>
        </template>
      </KvCard>
    </div>
  </div>
</template>

<script>
import BenefitsSidePanel from "../../shared/benefits-side-panel.vue";
import KvImageInput from "../../../components/inputs/kv-image-input.vue";
import BenefitsToggle from "../../shared/benefits-toggle.vue";
import KvInput from "../../../components/inputs/kv-input.vue";
import KvSelect from "../../../components/inputs/kv-select.vue";
import KvTextarea from "../../../components/inputs/kv-textarea.vue";
import KvCard from "../../../components/ui/kv-card.vue";
import BenefitsRewardCard from "./benefits-reward-card.vue";
import KvTooltip from "../../../components/ui/kv-tooltip.vue";
import BenefitsRichText from "../../shared/benefits-rich-text.vue";
import { tierSelectOptions } from "../tiers/tiers-utils";
import BenefitsTierChip from "../tiers/benefits-tier-chip.vue";

const SUCCESS_MESSAGE_MAX = 140;
const DEFAULT_PRIVACY = "all";

export default {
  name: "BenefitsPointRewardPanel",
  components: {
    BenefitsSidePanel,
    KvImageInput,
    BenefitsToggle,
    KvInput,
    KvSelect,
    KvTextarea,
    KvCard,
    BenefitsRewardCard,
    KvTooltip,
    BenefitsRichText,
    BenefitsTierChip,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    reward: {
      type: Object,
      default: null,
    },
    rewards: {
      type: Array,
      default: () => [],
    },
    tiers: {
      type: Array,
      default: () => [],
    },
    tiersEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      draft: this.createDraft(this.reward),
      panelOpen: this.value,
      deleteConfirmOpen: false,
      form: {
        submitting: false,
        error: null,
      },
      SUCCESS_MESSAGE_MAX,
    };
  },
  computed: {
    hasTiers() {
      return this.tierOptions.length > 0;
    },
    tierOptions() {
      return tierSelectOptions(this.tiers);
    },
    privacyOptions() {
      return [
        { value: "all", label: this.t("benefitsCenter-points-panel-privacyAll") },
        { value: "blurred", label: this.t("benefitsCenter-points-panel-privacyBlurred") },
        { value: "tier_only", label: this.t("benefitsCenter-points-panel-privacyTierOnly") },
      ];
    },
    successMessageLength() {
      return (this.draft.successMessage || "").length;
    },
    hasAnyUsageOption() {
      return this.draft.canUse || this.draft.hasUsageInstructions;
    },
    title() {
      return this.t(
        this.draft.id
          ? "benefitsCenter-points-panel-editTitle"
          : "benefitsCenter-points-panel-createTitle",
      );
    },
    canSave() {
      const hasName = this.draft.name.trim().length > 0;
      const hasPoints = this.draft.points !== null && this.draft.points >= 1;
      const hasPrivacy = !!this.draft.privacy;

      let hasValidInstructions = true;
      if (this.draft.hasUsageInstructions) {
        hasValidInstructions = this.draft.usageInstructions.trim().length > 0;
      }

      let hasValidSuccessMessage = true;
      if (this.draft.canUse) {
        hasValidSuccessMessage = this.draft.successMessage.trim().length > 0;
      }

      return (
        hasName &&
        hasPoints &&
        hasPrivacy &&
        this.hasAnyUsageOption &&
        hasValidInstructions &&
        hasValidSuccessMessage
      );
    },
    missingFieldsTooltip() {
      if (this.canSave) return "";

      const missing = [];
      if (!this.draft.name.trim()) missing.push(this.t("benefitsCenter-points-panel-name"));
      if (this.draft.points === null || this.draft.points < 1) missing.push(this.t("benefitsCenter-points-panel-points"));
      if (!this.draft.privacy) missing.push(this.t("benefitsCenter-points-panel-privacy"));

      if (!this.hasAnyUsageOption) {
        missing.push(this.t("benefitsCenter-points-panel-usageOption"));
      }

      if (this.draft.canUse && !this.draft.successMessage.trim()) {
        missing.push(this.t("benefitsCenter-points-panel-successMessage"));
      }

      if (this.draft.hasUsageInstructions && !this.draft.usageInstructions.trim()) {
        missing.push(this.t("benefitsCenter-points-panel-howToUseInstructions"));
      }

      if (missing.length === 0) return "";
      
      return this.t("benefitsCenter-points-panel-missingFieldsTooltip", { fields: missing.join(", ") });
    },
  },
  watch: {
    value(isOpen) {
      this.panelOpen = isOpen;
      if (isOpen) {
        this.draft = this.createDraft(this.reward);
      }
    },
    panelOpen(isOpen) {
      this.$emit("input", isOpen);
    },
    "draft.tierIds"(tierIds) {
      if (!tierIds.length) {
        this.draft.privacy = DEFAULT_PRIVACY;
      }
    },
    reward: {
      deep: true,
      handler(reward) {
        if (this.panelOpen) {
          this.draft = this.createDraft(reward);
        }
      },
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    createDraft(reward) {
      const safeReward = reward || {};

      return {
        id: safeReward.id || "",
        active: safeReward.active !== false,
        name: safeReward.name || "",
        subtitle: safeReward.subtitle || "",
        description: safeReward.description || "",
        points: safeReward.points || 0,
        privacy: safeReward.privacy || DEFAULT_PRIVACY,
        startDate: safeReward.startDate || "",
        endDate: safeReward.endDate || "",
        canUse: safeReward.canUse || false,
        hasRedemptions: Boolean(safeReward.hasRedemptions),
        successMessage: safeReward.successMessage || "",
        hasUsageInstructions: safeReward.hasUsageInstructions || false,
        usageInstructions: safeReward.usageInstructions || "",
        image: safeReward.image ? { ...safeReward.image } : null,
        tierIds: Array.isArray(safeReward.tierIds) ? [...safeReward.tierIds] : [],
      };
    },
    limitSuccessMessage(value) {
      const nextValue = String(value || "");

      if (nextValue.length > SUCCESS_MESSAGE_MAX) {
        this.draft.successMessage = nextValue.slice(0, SUCCESS_MESSAGE_MAX);
      }
    },
    close() {
      this.panelOpen = false;
      this.handleClose();
    },
    handleClose() {
      this.$emit("close");
    },
    save() {
      if (!this.canSave) return;

      this.$emit("save", {
        ...this.draft,
        id: this.draft.id,
        name: this.draft.name.trim(),
        subtitle: this.draft.subtitle.trim(),
        description: this.draft.description.trim(),
        successMessage: this.draft.successMessage.trim(),
        usageInstructions: this.draft.usageInstructions.trim(),
      });
      this.close();
    },
    deleteReward() {
      this.deleteConfirmOpen = true;
      this.panelOpen = false;
    },
    confirmDelete() {
      this.$emit("delete", this.draft);
      this.deleteConfirmOpen = false;
      this.handleClose(); 
    },
    closeDeleteConfirm() {
      this.deleteConfirmOpen = false;
      this.handleClose();
    },
  },
};
</script>

<style lang="scss" scoped>
.benefits-reward-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 4px;

  ::v-deep .kv-input,
  ::v-deep .kv-textarea,
  ::v-deep .kv-select,
  ::v-deep .kv-editor,
  ::v-deep .kv-image-input {
    width: 100%;
  }

  ::v-deep .kv-select .kv-popup-container {
    display: block;
    width: 100%;
  }
}

.benefits-reward-panel-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.benefits-reward-fields-row {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.benefits-reward-fields-row--meta {
  gap: 24px;
  grid-template-columns: minmax(0, 0.6fr) minmax(0, 1fr) minmax(0, 1fr);

  .benefits-reward-field {
    min-width: 0;
  }
}

.benefits-reward-fields-row--with-privacy {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.benefits-reward-field-control {
  min-width: 0;
  width: 100%;
}

.benefits-reward-fields-row--meta .benefits-reward-field-control {
  ::v-deep .kv-input,
  ::v-deep .kv-date-picker {
    display: block;
    width: 100%;
  }

  ::v-deep .kv-popup-container {
    display: block;
    width: 100%;
  }
}

.benefits-reward-field-label {
  align-items: center;
  color: #384250;
  display: flex;
  font-family: "Sora", sans-serif;
  font-size: 16px;
  font-weight: 500;
  gap: 8px;
  justify-content: space-between;
  line-height: 1.3;
  margin: 0;
  min-height: 24px;
}

.benefits-reward-field-label-text {
  align-items: center;
  display: inline-flex;
  gap: 2px;
}

.benefits-reward-clear-date {
  background: none;
  border: none;
  color: #6c737f;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  padding: 0;
  text-decoration: underline;
  white-space: nowrap;

  &:hover {
    color: #384250;
  }
}

@media (max-width: 520px) {
  .benefits-reward-fields-row--meta {
    grid-template-columns: minmax(0, 0.7fr) minmax(0, 1fr);
  }

  .benefits-reward-fields-row--meta .benefits-reward-field--points {
    grid-column: 1 / -1;
  }
}

.benefits-reward-field {
  color: #384250;
  display: flex;
  flex-direction: column;
  font-family: "Sora", sans-serif;
  font-size: 16px;
  font-weight: 500;
  gap: 12px;
  line-height: 1.3;
  margin: 0;

  &.checkbox-field {
    flex-direction: row;
    align-items: center;
    font-size: 14px;
    font-weight: 400;

    input {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }
  }
}

.benefits-reward-input {
  background: #f9fafb;
  border: 1px solid #1f2a37;
  border-radius: 8px;
  color: #1f2a37;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  height: 44px;
  line-height: 1.3;
  outline: none;
  padding: 12px;
  width: 100%;

  &:focus {
    border-color: #2aaa4c;
    box-shadow: 0 0 0 2px rgba(42, 170, 76, 0.14);
  }
}

.benefits-reward-textarea {
  height: auto;
  min-height: 96px;
  resize: vertical;
}

.benefits-reward-select-wrapper {
  position: relative;
}

.benefits-reward-select-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  cursor: pointer;
  background: #f9fafb;
  border: 1px solid #1f2a37;
  border-radius: 8px;
  color: #1f2a37;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  height: 44px;
  line-height: 1.3;
  outline: none;
  padding: 12px;

  &:focus {
    border-color: #2aaa4c;
    box-shadow: 0 0 0 2px rgba(42, 170, 76, 0.14);
  }
}

.benefits-reward-select-icon {
  pointer-events: none;
  transition: transform 0.2s ease;

  &.is-open {
    transform: rotate(180deg);
  }
}

.benefits-reward-select-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0px 5px 46.6px rgba(0, 0, 0, 0.25);
  padding: 8px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.benefits-reward-select-item {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #1f2a37;
  text-align: left;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: #f9fafb;
  }

  &.is-active {
    background-color: #f3f4f6;
    color: #1f2a37;
    font-weight: 600;
  }
}

.menu-fade-enter-active,
.menu-fade-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.menu-fade-enter,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.benefits-reward-toggles {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
}

.benefits-reward-toggle-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.benefits-reward-toggle-hint,
.benefits-reward-field-hint {
  color: #6c737f;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  margin: 0;
}

.benefits-reward-toggle-row {
  align-items: center;
  color: #384250;
  display: flex;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  gap: 12px;
  line-height: 1.2;
  margin: 0;
  cursor: pointer;
}

.benefits-reward-toggle-control {
  display: inline-flex;
}

.benefits-reward-toggle-input {
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
}

.benefits-reward-toggle {
  background: #ffffff;
  border: 0.5px solid #f3f4f6;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  height: 18px;
  padding: 1px 4px;
  position: relative;
  width: 40px;

  &::before {
    background: #d2d6db;
    border: 0.5px solid #9da4ae;
    border-radius: 3px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    content: "";
    height: 14px;
    left: 2px;
    position: absolute;
    top: 1px;
    transition: transform 0.15s ease;
    width: 16px;
  }
}

.benefits-reward-toggle-input:checked + .benefits-reward-toggle {
  background: #d3f8e0;
  border-color: #17b26a;

  &::before {
    background: #17b26a;
    border-color: #097347;
    transform: translateX(18px);
  }
}

.benefits-reward-toggle-input:focus-visible + .benefits-reward-toggle {
  outline: 2px solid rgba(42, 170, 76, 0.35);
  outline-offset: 2px;
}

.benefits-reward-panel-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.benefits-reward-panel-footer-left,
.benefits-reward-panel-footer-right {
  display: flex;
  gap: 12px;
  align-items: center;
}

.benefits-reward-panel-cancel {
  color: #1f2a37 !important;
}

.benefits-reward-panel-save {
  min-width: 120px;
}

.benefits-reward-panel-save {
  background-color: #2aaa4c !important;
  border-color: #2aaa4c !important;
  color: #ffffff !important;
}

.benefits-reward-panel-delete {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #fee4e2;
  color: #f04438;
  border: none;
  border-radius: 8px;
  padding: 0 16px;
  height: 40px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #fcd5d3;
  }

  &:disabled {
    background-color: #f3f4f6;
    color: #9da4ae;
    cursor: not-allowed;

    &:hover {
      background-color: #f3f4f6;
    }
  }

  .ui-icon {
    font-size: 18px;
  }
}

.benefits-required-asterisk {
  color: #f04438;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  margin-left: 2px;
}

.benefits-reward-counter {
  align-self: flex-end;
  color: #6c737f;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  line-height: 1.2;
  margin-top: 4px;
}

.benefits-reward-counter.is-full {
  color: #b54708;
}

.benefits-reward-toggles-alert {
  background: #fffaeb;
  border: 1px solid #fedf89;
  border-radius: 8px;
  color: #b54708;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  line-height: 1.4;
  margin: 0;
  padding: 10px 12px;
}

@media (max-width: 767px) {
  .benefits-reward-fields-row {
    grid-template-columns: 1fr;
  }

  .benefits-reward-panel-cancel {
    width: 100%;
  }

  .benefits-reward-panel-save {
    min-width: 0;
  }
}

.benefits-reward-delete-modal-backdrop {
  align-items: center;
  background: rgba(17, 25, 39, 0.4);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 10000;
}

::v-deep .benefits-reward-delete-modal {
  background: #FCFCFD !important;
  border: 1px solid #E5E7EB !important;
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
  max-width: 520px !important;
  width: 92% !important;
}

::v-deep .benefits-reward-delete-modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px !important;
  position: relative;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
}

.benefits-reward-delete-modal-close {
  background: transparent;
  border: none;
  color: #9da4ae;
  cursor: pointer;
  position: absolute;
  right: 12px;
  top: 12px;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: #f3f4f6;
    color: #4d5761;
  }
}

.benefits-reward-delete-modal-title {
  color: #4D5761;
  font-family: "Inter", sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0;
  width: 100%;
  text-align: center;
}

.benefits-reward-delete-modal-desc {
  color: #4D5761;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
  width: 100%;
  text-align: center;
}

.benefits-reward-delete-modal-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 0;
  gap: 12px;
}

.benefits-reward-delete-cancel {
  height: 44px !important;
  min-height: 44px !important;
  min-width: 120px !important;
  padding: 0 24px !important;
  color: #4D5761 !important;
  flex: 0 0 auto;
}

.benefits-reward-delete-confirm {
  align-items: center;
  background-color: #fee4e2;
  border: none;
  border-radius: 8px;
  color: #f04438;
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  min-width: 140px;
  padding: 0 28px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  height: 44px;
  justify-content: center;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #fcd5d3;
  }
}
</style>
